import { useEffect, useRef } from 'react';
import { useStore } from '@nanostores/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { $isAuthenticated } from '~/lib/stores/auth';
import { $activeGeneration, type ActiveGenerationInfo } from '~/lib/stores/activeGeneration';
import { chatId as chatIdStore } from '~/lib/persistence';
import { apiGetActiveGeneration } from '~/lib/api/chats';
import { createScopedLogger } from '~/utils/logger';

const logger = createScopedLogger('ActiveGeneration');
const POLL_INTERVAL_MS = 10_000;

/**
 * Polls the user's single in-flight generation and mirrors it into
 * `$activeGeneration` so badges across the app stay current. When a background
 * generation (one the user is NOT currently viewing) finishes, fires a toast
 * that jumps to the finished thread.
 */
export default function ActiveGenerationProvider() {
  const isAuthenticated = useStore($isAuthenticated);
  const navigate = useNavigate();
  const prevRef = useRef<ActiveGenerationInfo | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      $activeGeneration.set(null);
      prevRef.current = null;

      return undefined;
    }

    let cancelled = false;
    // Hide→show within one request would otherwise start a second poll that sees the same
    // finished generation and fires its "is ready" toast twice.
    let inFlight = false;

    const poll = async () => {
      if (inFlight) {
        return;
      }

      inFlight = true;

      try {
        const running = await apiGetActiveGeneration();

        if (cancelled) {
          return;
        }

        const prev = prevRef.current;

        // A previously-running generation is no longer the active one → it ended.
        // Toast only if the user isn't currently viewing it (they'd see it finish).
        if (prev && (!running || running.id !== prev.id)) {
          const viewingId = chatIdStore.get();
          const isViewing = viewingId === prev.id || viewingId === (prev.urlId ?? undefined);

          if (!isViewing) {
            const label = prev.description?.trim() || 'your build';
            const target = `/chat/${prev.urlId || prev.id}`;
            toast.success(`"${label}" is ready`, {
              onClick: () => navigate(target),
            });
          }
        }

        prevRef.current = running;
        $activeGeneration.set(running);
      } catch (err) {
        logger.warn('Failed to poll active generation', err);
      } finally {
        inFlight = false;
      }
    };

    // Pause while the tab is hidden (a backgrounded phone tab burns battery and data), resume on return.
    let interval: ReturnType<typeof setInterval> | undefined;

    const start = () => {
      if (interval) {
        return;
      }

      poll();
      interval = setInterval(poll, POLL_INTERVAL_MS);
    };

    const stop = () => {
      clearInterval(interval);
      interval = undefined;
    };

    const onVisibilityChange = () => (document.hidden ? stop() : start());

    if (!document.hidden) {
      start();
    }

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isAuthenticated, navigate]);

  return null;
}
